import { eachDayOfInterval, format, parseISO, startOfDay, type Locale } from "date-fns";
import type { DailySummaryListResponse } from "~/shared/api/daily-summary/daily-summary.api";
import type { MvoAnalyticsType } from "./mvo-analytics-section.types";
import { toDateParam } from "~/shared/utils/filterDateFormatter";
import {
  ReportBadgeColor,
  RECYCLING_POINT_BADGE_COLORS,
  toPercentBadge,
} from "~/shared/utils/badgeFormatters";
import { Namespace, TFn } from "@racoon/i18n";
import type { ConnectProto } from "@racoon/api";

type ReportStatItem = {
  id: string;
  badge: string;
  badgeColor: ReportBadgeColor;
  value: string | number;
  subtitle?: string;
  label: string;
  showInProgressBar?: boolean;
};

type ReportProgressBarConfig = {
  valueType?: "value" | "percentage";
  minSegmentPct?: number;
  minSegmentWidthPx?: number;
  hideLabelBelowPct?: number;
  formatValue?: "round" | "fixed2";
};

export type ReportDetailSection = {
  title: string;
  analyticsType: MvoAnalyticsType;
  items: ReportStatItem[];
  progressBar?: ReportProgressBarConfig;
};

export type ReportTotals = {
  stands_visited: number;
  stands_not_visited: number;
  stands_plan: number;
  vol_ordered: number;
  vol_not_visited: number;
  vol_not_distributed: number;
  vol_nominal_handled: number;
  vol_fact_handled: number;
  vol_confirmed_fail: number;
  count_ordered: number;
  count_fact: number;
  count_confirmed_fail: number;
  count_not_distributed: number;
  count_not_visited: number;
  veh_total_count: number;
  veh_unit_count: number;
  veh_garbage_truck_count: number;
  veh_working: number;
  veh_waiting: number;
};

const RECYCLING_POINT_CHART_COLORS: { border: string; fill: string }[] = [
  { border: "#22c55e", fill: "rgba(34, 197, 94, 0.22)" },
  { border: "#ef4444", fill: "rgba(239, 68, 68, 0.22)" },
  { border: "#a855f7", fill: "rgba(168, 85, 247, 0.22)" },
  { border: "#3b82f6", fill: "rgba(59, 130, 246, 0.22)" },
  { border: "#f59e0b", fill: "rgba(245, 158, 11, 0.22)" },
  { border: "#06b6d4", fill: "rgba(6, 182, 212, 0.22)" },
];

/**
 * Categorical palette for disposal sites, in fixed slot order. A site's colour
 * comes from its own identity (see buildDisposalSiteColors), never from its rank
 * in the current response, so filtering providers or changing the date range
 * never repaints the sites that remain on screen.
 *
 * The order is the CVD-safety mechanism, not cosmetic: neighbouring slots land
 * next to each other in stacked bars, so green is kept away from orange/pink.
 * Validated on a white surface — worst adjacent pair ΔE 13.2 (deuteranopia),
 * 24.0 (normal vision). Re-run the check before reordering or adding hues.
 */
export const DISPOSAL_SITE_PALETTE = [
  "#32ADE6", // blue
  "#FF9500", // orange
  "#5856D6", // indigo
  "#34C759", // green
  "#AF52DE", // purple
  "#D68000", // amber
  "#00A3A3", // teal
  "#FF2D55", // pink
] as const;

const disposalSiteColorAt = (index: number) =>
  index < DISPOSAL_SITE_PALETTE.length
    ? DISPOSAL_SITE_PALETTE[index]
    : // More sites than palette slots: step the hue by the golden angle so the
      // generated colours stay far apart from one another, at the palette's own
      // lightness/chroma so they don't stand out as a different family.
      `hsl(${((index * 137.508) % 360).toFixed(1)} 65% 45%)`;

/**
 * One distinct colour per unique disposal site, keyed by disposal-site id.
 *
 * Sites are collected across every provider and ordered by id, so the same site
 * keeps the same colour in every card, in the legend, and in the PDF export.
 */
export const buildDisposalSiteColors = (
  disposalVisitPerProvider: { disposalVisits?: { disposalSiteId?: unknown }[] }[],
): Map<string, string> => {
  const siteIds = new Set<string>();
  disposalVisitPerProvider.forEach((provider) => {
    provider.disposalVisits?.forEach((visit) => {
      const siteId = String(visit.disposalSiteId ?? "");
      if (siteId) siteIds.add(siteId);
    });
  });

  const colorBySiteId = new Map<string, string>();
  Array.from(siteIds)
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
    .forEach((siteId, index) => colorBySiteId.set(siteId, disposalSiteColorAt(index)));
  return colorBySiteId;
};

export const PAGE_TITLES = (t: TFn): Record<MvoAnalyticsType, string> => ({
  stands: t(Namespace.Govdash, "analytics.subtitle.stands") || "Площадки",
  volumes: t(Namespace.Govdash, "analytics.subtitle.volumes") || "Объемы",
  containers: t(Namespace.Govdash, "analytics.subtitle.containers") || "Контейнеры",
  equipment: t(Namespace.Govdash, "analytics.subtitle.equipment") || "Спецтехника",
  recycling: t(Namespace.Govdash, "analytics.subtitle.recycling") || "Переработка и захоронение",
});

const MAX_X_AXIS_TICKS = 5;

const roundTo2 = (value: number) => Number(value.toFixed(2));
export const formatTo2 = (value: number) => value.toFixed(2);

const fieldNum = (row: any, snake: string, camel: string): number => {
  const raw = row?.[snake] ?? row?.[camel];
  if (raw == null) return 0;
  if (typeof raw === "bigint") return Number(raw);
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
};

const standsVolumesContainersRaw = (
  row: any | null,
  kind: "stands" | "volumes" | "containers",
): { handled: number; inProgress: number; notRemoved: number } => {
  const r = row ?? {};
  let handledRaw = 0;
  let inProgressRaw = 0;
  let notRemovedRaw = 0;

  if (kind === "stands") {
    handledRaw = fieldNum(r, "stands_visited", "standsVisited");
    inProgressRaw = fieldNum(r, "stands_not_visited", "standsNotVisited");
  } else if (kind === "volumes") {
    handledRaw = fieldNum(r, "vol_nominal_handled", "volNominalHandled");
    inProgressRaw =
      fieldNum(r, "vol_not_visited", "volNotVisited") +
      fieldNum(r, "vol_not_distributed", "volNotDistributed");
    notRemovedRaw = fieldNum(r, "vol_confirmed_fail", "volConfirmedFail");
  } else {
    handledRaw = fieldNum(r, "count_fact", "countFact");
    inProgressRaw =
      fieldNum(r, "count_not_visited", "countNotVisited") +
      fieldNum(r, "count_not_distributed", "countNotDistributed");
    notRemovedRaw = fieldNum(r, "count_confirmed_fail", "countConfirmedFail");
  }

  return { handled: handledRaw, inProgress: inProgressRaw, notRemoved: notRemovedRaw };
};

export const orderedRowsFromSummary = (summaryData?: DailySummaryListResponse) => {
  if (!summaryData) return [];
  return Object.keys(summaryData)
    .sort((a, b) => Number(a) - Number(b))
    .map((k) => summaryData[k]);
};

const normalizeVisitPointName = (point: any): string =>
  String(point?.point_name ?? point?.pointName ?? "").trim() || "Без названия";

export const aggregateVisitPointStats = (rows: any[]) => {
  const aggregated = new Map<string, number>();
  rows.forEach((item: any) => {
    const stats = Array.isArray(item?.visit_point_stats) ? item.visit_point_stats : [];
    stats.forEach(
      (point: { point_name?: string; pointName?: string; volume?: number | string }) => {
        const pointName = normalizeVisitPointName(point);
        const volume = Number(point?.volume ?? 0);
        aggregated.set(pointName, (aggregated.get(pointName) ?? 0) + volume);
      },
    );
  });

  return Array.from(aggregated.entries())
    .map(([point_name, volume]) => ({ point_name, volume }))
    .sort((a, b) => Number(b?.volume ?? 0) - Number(a?.volume ?? 0));
};

const getVisitPointTotal = (item: any) => {
  const stats = Array.isArray(item?.visit_point_stats) ? item.visit_point_stats : [];
  return stats.reduce((sum: number, point: any) => sum + Number(point?.volume ?? 0), 0);
};

const recyclingVisitPointOrder = (rows: any[]): string[] => {
  const aggregated = new Map<string, number>();
  for (const row of rows) {
    const stats = Array.isArray(row?.visit_point_stats) ? row.visit_point_stats : [];
    for (const p of stats) {
      const name = normalizeVisitPointName(p);
      aggregated.set(name, (aggregated.get(name) ?? 0) + Number(p?.volume ?? 0));
    }
  }
  return Array.from(aggregated.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([name]) => name);
};

const visitPointVolumeForDay = (row: any | null, pointName: string): number => {
  if (!row) return 0;
  const stats = Array.isArray(row?.visit_point_stats) ? row.visit_point_stats : [];
  for (const p of stats) {
    if (normalizeVisitPointName(p) === pointName) return Number(p?.volume ?? 0);
  }
  return 0;
};

const buildPrimaryUnloadingItems = (
  visitPointStats: Array<{ point_name?: string; volume?: number | string }>,
): ReportStatItem[] => {
  const totalVolume = visitPointStats.reduce((sum, point) => sum + Number(point?.volume ?? 0), 0);
  return visitPointStats.map((point, index) => {
    const volume = Number(point?.volume ?? 0);
    return {
      id: `recycling_point_${index + 1}`,
      badge: toPercentBadge(volume, totalVolume),
      badgeColor: RECYCLING_POINT_BADGE_COLORS[index % RECYCLING_POINT_BADGE_COLORS.length],
      value: roundTo2(volume),
      subtitle: `${formatTo2(volume)} м³`,
      label: point?.point_name || `Пункт ${index + 1}`,
      showInProgressBar: true,
    };
  });
};

export const buildDetailSections = (
  totalSummary: ReportTotals,
  totalTrucks: number,
  visitPointStats: Array<{ point_name?: string; volume?: number | string }>,
  t: TFn,
  // Distinct trucks on line, from GovDashService/DriverOnShifts.
  activeTrucks: number,
): ReportDetailSection[] => {
  const primaryUnloadingItems = buildPrimaryUnloadingItems(visitPointStats);
  const offlineTrucks = Math.max(totalTrucks - activeTrucks, 0);
  return [
    {
      title: PAGE_TITLES(t).stands,
      analyticsType: "stands",
      progressBar: { valueType: "value", formatValue: "round" },
      items: [
        {
          id: "stands_plan",
          badge: t(Namespace.Govdash, "analytics.status.plan") || "План",
          badgeColor: "bg-gray-800",
          value: roundTo2(totalSummary.stands_plan),
          label: t(Namespace.Govdash, "analytics.status.planned") || "Запланировано",
        },
        {
          id: "stands_exported",
          badge: toPercentBadge(totalSummary.stands_visited, totalSummary.stands_plan),
          badgeColor: "bg-green-500",
          value: roundTo2(totalSummary.stands_visited),
          label: t(Namespace.Govdash, "analytics.status.exported") || "Вывезено",
          showInProgressBar: true,
        },
        {
          id: "stands_in_progress",
          badge: toPercentBadge(totalSummary.stands_not_visited, totalSummary.stands_plan),
          badgeColor: "bg-blue-500",
          value: roundTo2(totalSummary.stands_not_visited),
          label: t(Namespace.Govdash, "analytics.status.inProgress") || "В процессе",
          showInProgressBar: true,
        },
      ],
    },
    {
      title: PAGE_TITLES(t).volumes,
      analyticsType: "volumes",
      progressBar: { valueType: "value", hideLabelBelowPct: 12, formatValue: "round" },
      items: [
        {
          id: "volumes_plan",
          badge: t(Namespace.Govdash, "analytics.status.plan") || "План",
          badgeColor: "bg-gray-800",
          value: roundTo2(totalSummary.vol_ordered),
          subtitle: t(Namespace.Govdash, "status.volumeMetric", {
            volume: formatTo2(totalSummary.vol_ordered),
          }),
          label: t(Namespace.Govdash, "analytics.status.planned") || "Запланировано",
        },
        {
          id: "volumes_exported",
          badge: toPercentBadge(totalSummary.vol_nominal_handled, totalSummary.vol_ordered),
          badgeColor: "bg-green-500",
          value: roundTo2(totalSummary.vol_nominal_handled),
          subtitle: t(Namespace.Govdash, "status.volumeMetric", {
            volume: formatTo2(totalSummary.vol_nominal_handled),
          }),
          label: t(Namespace.Govdash, "analytics.status.exported") || "Вывезено",
          showInProgressBar: true,
        },
        {
          id: "volumes_in_progress",
          badge: toPercentBadge(
            totalSummary.vol_not_visited + totalSummary.vol_not_distributed,
            totalSummary.vol_ordered,
          ),
          badgeColor: "bg-blue-500",
          value: roundTo2(totalSummary.vol_not_visited + totalSummary.vol_not_distributed),
          subtitle: t(Namespace.Govdash, "status.volumeMetric", {
            volume: formatTo2(totalSummary.vol_not_visited + totalSummary.vol_not_distributed),
          }),
          label: t(Namespace.Govdash, "analytics.status.inProgress") || "В процессе",
          showInProgressBar: true,
        },
        {
          id: "volumes_not_exported",
          badge: toPercentBadge(totalSummary.vol_confirmed_fail, totalSummary.vol_ordered),
          badgeColor: "bg-red-500",
          value: roundTo2(totalSummary.vol_confirmed_fail),
          subtitle: t(Namespace.Govdash, "status.volumeMetric", {
            volume: formatTo2(totalSummary.vol_confirmed_fail),
          }),
          label: t(Namespace.Govdash, "analytics.status.notExported") || "Невывоз",
          showInProgressBar: true,
        },
      ],
    },
    {
      title: PAGE_TITLES(t).containers,
      analyticsType: "containers",
      progressBar: { valueType: "value", hideLabelBelowPct: 12, formatValue: "round" },
      items: [
        {
          id: "containers_plan",
          badge: t(Namespace.Govdash, "analytics.status.plan") || "План",
          badgeColor: "bg-gray-800",
          value: roundTo2(totalSummary.count_ordered),
          subtitle: t(Namespace.Govdash, "status.count", {
            count: totalSummary.count_ordered,
          }),
          label: t(Namespace.Govdash, "analytics.status.planned") || "Запланировано",
        },
        {
          id: "containers_exported",
          badge: toPercentBadge(totalSummary.count_fact, totalSummary.count_ordered),
          badgeColor: "bg-green-500",
          value: roundTo2(totalSummary.count_fact),
          subtitle: t(Namespace.Govdash, "status.count", {
            count: totalSummary.count_fact,
          }),
          label: t(Namespace.Govdash, "analytics.status.exported") || "Вывезено",
          showInProgressBar: true,
        },
        {
          id: "containers_in_progress",
          badge: toPercentBadge(
            totalSummary.count_not_visited + totalSummary.count_not_distributed,
            totalSummary.count_ordered,
          ),
          badgeColor: "bg-blue-500",
          value: roundTo2(totalSummary.count_not_visited + totalSummary.count_not_distributed),
          subtitle: t(Namespace.Govdash, "status.count", {
            count: totalSummary.count_not_visited + totalSummary.count_not_distributed,
          }),
          label: t(Namespace.Govdash, "analytics.status.inProgress") || "В процессе",
          showInProgressBar: true,
        },
        {
          id: "containers_not_exported",
          badge: toPercentBadge(totalSummary.count_confirmed_fail, totalSummary.count_ordered),
          badgeColor: "bg-red-500",
          value: roundTo2(totalSummary.count_confirmed_fail),
          subtitle: t(Namespace.Govdash, "status.count", {
            count: totalSummary.count_confirmed_fail,
          }),
          label: t(Namespace.Govdash, "analytics.status.notExported") || "Невывоз",
          showInProgressBar: true,
        },
      ],
    },
    {
      title: PAGE_TITLES(t).equipment,
      analyticsType: "equipment",
      progressBar: { valueType: "value", hideLabelBelowPct: 12, formatValue: "round" },
      items: [
        {
          id: "equipment_total",
          badge: t(Namespace.Govdash, "analytics.status.plan") || "План",
          badgeColor: "bg-gray-800",
          value: roundTo2(totalTrucks),
          label: t(Namespace.Govdash, "analytics.status.totalVehicles") || "Всего машин",
        },
        {
          id: "equipment_online",
          badge: toPercentBadge(activeTrucks, totalTrucks),
          badgeColor: "bg-green-500",
          value: activeTrucks,
          label: t(Namespace.Govdash, "analytics.status.vehiclesOnLine") || "На линии",
          showInProgressBar: true,
        },
        {
          id: "equipment_offline",
          badge: toPercentBadge(offlineTrucks, totalTrucks),
          badgeColor: "bg-text-secondary",
          value: offlineTrucks,
          label: t(Namespace.Govdash, "analytics.status.vehiclesIdle") || "Не на линии",
          showInProgressBar: true,
        },
      ],
    },
    {
      title: PAGE_TITLES(t).recycling,
      analyticsType: "recycling",
      progressBar: { valueType: "percentage", minSegmentPct: 10, minSegmentWidthPx: 35 },
      items: primaryUnloadingItems,
    },
  ];
};

export const aggregateTotalSummary = (rows: any[]): ReportTotals => {
  const aggregated: ReportTotals = {
    stands_visited: 0,
    stands_not_visited: 0,
    stands_plan: 0,
    vol_ordered: 0,
    vol_not_visited: 0,
    vol_not_distributed: 0,
    vol_nominal_handled: 0,
    vol_fact_handled: 0,
    vol_confirmed_fail: 0,
    count_ordered: 0,
    count_fact: 0,
    count_confirmed_fail: 0,
    count_not_distributed: 0,
    count_not_visited: 0,
    veh_total_count: 0,
    veh_unit_count: 0,
    veh_garbage_truck_count: 0,
    veh_working: 0,
    veh_waiting: 0,
  };

  rows.forEach((item: any) => {
    aggregated.stands_visited += Number(item?.stands_visited ?? 0);
    aggregated.stands_not_visited += Number(item?.stands_not_visited ?? 0);
    aggregated.stands_plan += Number(item?.stands_plan ?? 0);
    aggregated.vol_ordered += Number(item?.vol_ordered ?? 0);
    aggregated.vol_not_visited += Number(item?.vol_not_visited ?? 0);
    aggregated.vol_not_distributed += Number(item?.vol_not_distributed ?? 0);
    aggregated.vol_nominal_handled += Number(item?.vol_nominal_handled ?? 0);
    aggregated.vol_fact_handled += Number(item?.vol_fact_handled ?? 0);
    aggregated.vol_confirmed_fail += Number(item?.vol_confirmed_fail ?? 0);
    aggregated.count_ordered += Number(item?.count_ordered ?? 0);
    aggregated.count_fact += Number(item?.count_fact ?? 0);
    aggregated.count_confirmed_fail += Number(item?.count_confirmed_fail ?? 0);
    aggregated.count_not_distributed += Number(item?.count_not_distributed ?? 0);
    aggregated.count_not_visited += Number(item?.count_not_visited ?? 0);
    aggregated.veh_total_count += Number(item?.veh_total_count ?? 0);
    aggregated.veh_unit_count += Number(item?.veh_unit_count ?? 0);
    aggregated.veh_garbage_truck_count += Number(item?.veh_garbage_truck_count ?? 0);
    aggregated.veh_working += Number(item?.veh_working ?? 0);
    aggregated.veh_waiting += Number(item?.veh_waiting ?? 0);
  });

  return aggregated;
};

export type OrganizationOption = {
  value: string;
  label: string;
};

export type PlanFactOrganizationSummary = {
  ownerOrganizationId: string;
  organizationName: string;
  totals: ReportTotals;
};

const getOwnerOrganizationId = (row: any): string | null => {
  const rawId = row?.owner_organization_id ?? row?.ownerOrganizationId;
  if (rawId == null) return null;
  return String(rawId);
};

export const aggregateSummaryByOrganization = (
  rows: any[],
  organizationOptions: OrganizationOption[],
): PlanFactOrganizationSummary[] => {
  const labelByOrganizationId = new Map(
    organizationOptions.map((option) => [option.value, option.label]),
  );

  const groupedRows = new Map<string, any[]>();

  rows.forEach((row) => {
    const ownerOrganizationId = getOwnerOrganizationId(row);
    if (!ownerOrganizationId) return;

    const existingRows = groupedRows.get(ownerOrganizationId);
    if (existingRows) {
      existingRows.push(row);
    } else {
      groupedRows.set(ownerOrganizationId, [row]);
    }
  });

  return Array.from(groupedRows.entries()).map(([ownerOrganizationId, organizationRows]) => ({
    ownerOrganizationId,
    organizationName: labelByOrganizationId.get(ownerOrganizationId) ?? ownerOrganizationId,
    totals: aggregateTotalSummary(organizationRows),
  }));
};

const rowDateKey = (row: any): string | null => {
  const raw =
    row?.date ??
    row?.summary_date ??
    row?.summaryDate ??
    row?.day ??
    row?.reportDate ??
    row?.report_date;
  if (raw == null) return null;

  if (typeof raw === "object" && raw !== null && "seconds" in raw) {
    const sec = Number((raw as { seconds?: unknown }).seconds);
    if (Number.isFinite(sec)) return toDateParam(startOfDay(new Date(sec * 1000)));
  }
  if (typeof raw === "bigint") {
    const n = Number(raw);
    const ms = n > 1e12 ? n : n * 1000;
    return toDateParam(startOfDay(new Date(ms)));
  }
  if (typeof raw === "number" && Number.isFinite(raw)) {
    const ms = raw > 1e12 ? raw : raw * 1000;
    return toDateParam(startOfDay(new Date(ms)));
  }
  if (typeof raw === "string") {
    const m = raw.match(/^(\d{4}-\d{2}-\d{2})/);
    if (m) return m[1];
    try {
      const parsed = parseISO(raw);
      if (!isNaN(parsed.getTime())) return toDateParam(startOfDay(parsed));
    } catch {
      return null;
    }
  }

  return null;
};

const buildRowForTimelineIndex = (timeline: Date[], orderedRows: any[]) => {
  const rowByDate = new Map<string, any>();
  orderedRows.forEach((row) => {
    const key = rowDateKey(row);
    if (key) rowByDate.set(key, row);
  });
  let matchCount = 0;
  for (let i = 0; i < timeline.length; i++) {
    if (rowByDate.has(toDateParam(timeline[i]))) matchCount++;
  }
  const useDateKeys = matchCount > 0;
  const useIndex = !useDateKeys && orderedRows.length === timeline.length;
  const useSingle = !useDateKeys && orderedRows.length === 1;

  return (i: number) => {
    if (useDateKeys) return rowByDate.get(toDateParam(timeline[i])) ?? null;
    if (useIndex) return orderedRows[i] ?? null;
    if (useSingle) return orderedRows[0] ?? null;
    return rowByDate.get(toDateParam(timeline[i])) ?? null;
  };
};

const shiftDateKey = (shift: ConnectProto.DriverOnShift) => {
  const ts = shift.startedAt ?? shift.twStartAt;
  return ts ? toDateParam(new Date(Number(ts.seconds) * 1000)) : null;
};

/** Distinct trucks per timeline day, bucketed by the local day each shift started. */
const countActiveTrucksByDay = (
  timeline: Date[],
  shifts: ConnectProto.DriverOnShift[],
): number[] => {
  const trucksByDay = new Map<string, Set<string>>();
  for (const shift of shifts) {
    const key = shiftDateKey(shift);
    if (!key) continue;
    const trucks = trucksByDay.get(key) ?? new Set<string>();
    trucks.add(String(shift.truckId));
    trucksByDay.set(key, trucks);
  }
  return timeline.map((day) => trucksByDay.get(toDateParam(day))?.size ?? 0);
};

export const buildTimeline = (start: Date, end: Date): Date[] => {
  const a = startOfDay(start);
  const b = startOfDay(end);
  if (a.getTime() === b.getTime()) return [a];
  if (a > b) return eachDayOfInterval({ start: b, end: a });
  return eachDayOfInterval({ start: a, end: b });
};

const formatTickLabel = (d: Date, compact: boolean, locale: Locale) =>
  compact
    ? `${format(d, "d MMM", { locale })}\n${format(d, "EEEEEE", { locale })}`
    : `${format(d, "d", { locale })}\n${format(d, "EEEEEE", { locale })}`;

const getVisibleTickIndices = (pointCount: number): Set<number> => {
  if (pointCount <= 0) return new Set();
  if (pointCount === 1) return new Set([0]);
  const k = Math.min(MAX_X_AXIS_TICKS, pointCount);
  const next = new Set<number>();
  for (let i = 0; i < k; i++) {
    next.add(Math.round((i * (pointCount - 1)) / Math.max(1, k - 1)));
  }
  return next;
};

export const buildChartLegend = (analyticsType: MvoAnalyticsType, orderedRows: any[], t: TFn) => {
  if (analyticsType === "equipment") {
    return [
      {
        id: "equipment_online",
        label: t(Namespace.Govdash, "analytics.status.vehiclesOnLine") || "На линии",
        color: "bg-green-500",
      },
      {
        id: "equipment_offline",
        label: t(Namespace.Govdash, "analytics.status.vehiclesIdle") || "Не на линии",
        color: "bg-text-secondary",
      },
    ];
  }
  if (analyticsType === "recycling") {
    const pointNames = recyclingVisitPointOrder(orderedRows);
    if (pointNames.length > 1) {
      return pointNames.map((name, i) => ({
        label: name,
        color: RECYCLING_POINT_BADGE_COLORS[i % RECYCLING_POINT_BADGE_COLORS.length],
      }));
    }
    return [
      {
        id:
          pointNames.length === 1
            ? pointNames[0]
            : t(Namespace.Govdash, "analytics.volumeByPoints") || "Объем по точкам",
        label:
          pointNames.length === 1
            ? pointNames[0]
            : t(Namespace.Govdash, "analytics.volumeByPoints") || "Объем по точкам",
        color: RECYCLING_POINT_BADGE_COLORS[0],
      },
    ];
  }
  if (analyticsType === "stands") {
    return [
      {
        id: "stands_exported",
        label: t(Namespace.Govdash, "analytics.status.exported") || "Вывезено",
        color: "bg-green-500",
      },
      {
        id: "stands_in_progress",
        label: t(Namespace.Govdash, "analytics.status.inProgress") || "В процессе",
        color: "bg-blue-500",
      },
    ];
  }
  return [
    {
      id: `${analyticsType}_exported`,
      label: t(Namespace.Govdash, "analytics.status.exported") || "Вывезено",
      color: "bg-green-500",
    },
    {
      id: `${analyticsType}_in_progress`,
      label: t(Namespace.Govdash, "analytics.status.inProgress") || "В процессе",
      color: "bg-blue-500",
    },
    {
      id: `${analyticsType}_not_exported`,
      label: t(Namespace.Govdash, "analytics.status.notExported") || "Невывоз",
      color: "bg-red-500",
    },
  ];
};

const xTickPlugin = (labels: string[], visible: Set<number>) => ({
  offset: false,
  bounds: "data" as const,
  grid: { display: false },
  ticks: {
    autoSkip: false,
    maxRotation: 0,
    padding: 2,
    callback: (_tick: unknown, index: number) => (visible.has(index) ? labels[index] : ""),
  },
});

const multilineXAxisLayout = () => ({
  padding: { top: 8, bottom: 32 },
});

const withYAxisHeadroom = (values: number[], minHeadroom = 1) => {
  const maxValue = values.reduce((max, v) => (Number.isFinite(v) ? Math.max(max, v) : max), 0);
  const dynamicHeadroom = Math.ceil(maxValue * 0.15);
  return maxValue + Math.max(minHeadroom, dynamicHeadroom);
};

export const buildChartConfig = ({
  analyticsType,
  rangeStart,
  rangeEnd,
  orderedRows,
  totalTrucksBaseline,
  driverOnShifts,
  dateLocale,
  /** PDF charts: x-axis shows day + weekday only (no month on the tick). */
  xAxisOmitMonth = false,
  t,
}: {
  analyticsType: MvoAnalyticsType;
  rangeStart: Date;
  rangeEnd: Date;
  orderedRows: any[];
  totalTrucksBaseline: number;
  // Trucks on line per day are counted from these shifts.
  driverOnShifts: ConnectProto.DriverOnShift[];
  dateLocale: Locale;
  xAxisOmitMonth?: boolean;
  t: TFn;
}) => {
  const timeline = buildTimeline(rangeStart, rangeEnd);
  const rowForIndex = buildRowForTimelineIndex(timeline, orderedRows);
  const pointCount = timeline.length;
  const compactLabels = pointCount > 14;
  const labels = timeline.map((d) =>
    xAxisOmitMonth
      ? `${format(d, "dd", { locale: dateLocale })}\n${format(d, "EEEEEE", { locale: dateLocale })}`
      : formatTickLabel(d, compactLabels, dateLocale),
  );
  const visibleTickIndices = getVisibleTickIndices(pointCount);
  const tooltipTitle = (items: any[]) => {
    const i = items[0]?.dataIndex;
    if (typeof i !== "number" || !timeline[i]) return "";
    return format(timeline[i], "d MMMM yyyy", { locale: dateLocale });
  };

  if (analyticsType === "recycling") {
    const pointNames = recyclingVisitPointOrder(orderedRows);
    const rowFor = rowForIndex;

    const recyclingDatasets =
      pointNames.length > 1
        ? pointNames.map((name, index) => {
            const c = RECYCLING_POINT_CHART_COLORS[index % RECYCLING_POINT_CHART_COLORS.length];
            return {
              label: name,
              data: timeline.map((_, i) => visitPointVolumeForDay(rowFor(i), name)),
              borderColor: c.border,
              backgroundColor: c.fill,
              fill: true,
              tension: 0.35,
              pointRadius: pointCount === 1 ? 4 : 2,
            };
          })
        : [
            {
              id:
                pointNames.length === 1
                  ? pointNames[0]
                  : t(Namespace.Govdash, "analytics.volumeByPoints") || "Объем по точкам",
              label:
                pointNames.length === 1
                  ? pointNames[0]
                  : t(Namespace.Govdash, "analytics.volumeByPoints") || "Объем по точкам",
              data: timeline.map((_, i) => {
                const row = rowFor(i);
                return pointNames.length === 1
                  ? visitPointVolumeForDay(row, pointNames[0])
                  : getVisitPointTotal(row);
              }),
              borderColor: RECYCLING_POINT_CHART_COLORS[0].border,
              backgroundColor: RECYCLING_POINT_CHART_COLORS[0].fill,
              fill: true,
              tension: 0.35,
              pointRadius: pointCount === 1 ? 4 : 2,
            },
          ];

    const recyclingMax = recyclingDatasets.reduce((acc, ds) => {
      const datasetMax = (ds.data as number[]).reduce(
        (max, v) => (Number.isFinite(v) ? Math.max(max, v) : max),
        0,
      );
      return Math.max(acc, datasetMax);
    }, 0);

    return {
      data: { labels, datasets: recyclingDatasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: multilineXAxisLayout(),
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              title: tooltipTitle,
              label: (ctx: any) =>
                typeof ctx.parsed?.y === "number"
                  ? `${ctx.dataset?.label ? `${ctx.dataset.label}: ` : ""}${ctx.parsed.y.toFixed(1)} м³`
                  : "",
            },
          },
        },
        scales: {
          x: xTickPlugin(labels, visibleTickIndices),
          y: { beginAtZero: true, suggestedMax: withYAxisHeadroom([recyclingMax]) },
        },
      },
    };
  }

  if (analyticsType === "equipment") {
    const online = countActiveTrucksByDay(timeline, driverOnShifts);
    const offline = online.map((count) => Math.max(totalTrucksBaseline - count, 0));

    const equipmentValues = [...online, ...offline];
    return {
      data: {
        labels,
        datasets: [
          {
            id: "equipment_online",
            label: t(Namespace.Govdash, "analytics.status.vehiclesOnLine") || "На линии",
            data: online,
            borderColor: "#22c55e",
            backgroundColor: "rgba(34, 197, 94, 0.24)",
            fill: true,
            stack: "s1",
            tension: 0.35,
            pointRadius: pointCount === 1 ? 4 : 2,
          },
          {
            id: "equipment_offline",
            label: t(Namespace.Govdash, "analytics.status.vehiclesIdle") || "Не на линии",
            data: offline,
            borderColor: "#727986",
            backgroundColor: "rgba(114, 121, 134, 0.24)",
            fill: true,
            stack: "s2",
            tension: 0.35,
            pointRadius: pointCount === 1 ? 4 : 2,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: multilineXAxisLayout(),
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              title: tooltipTitle,
              label: (ctx: any) =>
                typeof ctx.parsed?.y === "number"
                  ? `${ctx.dataset?.label ? `${ctx.dataset.label}: ` : ""}${ctx.parsed.y.toFixed(0)}`
                  : "",
            },
          },
        },
        scales: {
          x: xTickPlugin(labels, visibleTickIndices),
          y: { beginAtZero: true, suggestedMax: withYAxisHeadroom(equipmentValues) },
        },
      },
    };
  }

  const stackKind: "stands" | "volumes" | "containers" =
    analyticsType === "stands" ? "stands" : analyticsType === "volumes" ? "volumes" : "containers";
  const rawRows = timeline.map((_, i) => standsVolumesContainersRaw(rowForIndex(i), stackKind));
  const handled = rawRows.map((p) => p.handled);
  const inProgress = rawRows.map((p) => p.inProgress);
  const notRemoved = rawRows.map((p) => p.notRemoved);
  const isStands = stackKind === "stands";

  return {
    data: {
      labels,
      datasets: isStands
        ? [
            {
              id: "stands_exported",
              label: t(Namespace.Govdash, "analytics.status.exported") || "Вывезено",
              data: handled,
              borderColor: "#22c55e",
              backgroundColor: "rgba(34, 197, 94, 0.24)",
              fill: true,
              stack: "s1",
              order: 2,
              tension: 0.5,
              pointRadius: pointCount === 1 ? 4 : 2,
            },
            {
              id: "stands_in_progress",
              label: t(Namespace.Govdash, "analytics.status.inProgress") || "В процессе",
              data: inProgress,
              borderColor: "#3b82f6",
              backgroundColor: "rgba(59, 130, 246, 0.24)",
              fill: true,
              stack: "s2",
              order: 1,
              tension: 0.5,
              pointRadius: pointCount === 1 ? 4 : 2,
            },
          ]
        : [
            {
              id: `${analyticsType}_exported`,
              label: t(Namespace.Govdash, "analytics.status.exported") || "Вывезено",
              data: handled,
              borderColor: "#22c55e",
              backgroundColor: "rgba(34, 197, 94, 0.24)",
              fill: true,
              stack: "s1",
              order: 3,
              tension: 0.5,
              pointRadius: pointCount === 1 ? 4 : 2,
            },
            {
              id: `${analyticsType}_in_progress`,
              label: t(Namespace.Govdash, "analytics.status.inProgress") || "В процессе",
              data: inProgress,
              borderColor: "#3b82f6",
              backgroundColor: "rgba(59, 130, 246, 0.24)",
              fill: true,
              stack: "s2",
              order: 2,
              tension: 0.5,
              pointRadius: pointCount === 1 ? 4 : 2,
            },
            {
              id: `${analyticsType}_not_exported`,
              label: t(Namespace.Govdash, "analytics.status.notExported") || "Невывоз",
              data: notRemoved,
              borderColor: "#ef4444",
              backgroundColor: "rgba(239, 68, 68, 0.24)",
              fill: true,
              stack: "s3",
              order: 1,
              tension: 0.5,
              pointRadius: pointCount === 1 ? 4 : 2,
            },
          ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      layout: multilineXAxisLayout(),
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: tooltipTitle,
            label: (ctx: any) =>
              typeof ctx.parsed?.y === "number"
                ? `${ctx.dataset?.label ? `${ctx.dataset.label}: ` : ""}${ctx.parsed.y.toFixed(0)}`
                : "",
          },
        },
      },
      scales: {
        x: xTickPlugin(labels, visibleTickIndices),
        y: {
          beginAtZero: true,
          suggestedMax: withYAxisHeadroom([...handled, ...inProgress, ...notRemoved]),
        },
      },
    },
  };
};
